import React, { FC, HTMLAttributes, SyntheticEvent, useCallback, useState } from 'react';
import { AutocompleteOwnerState, AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import { PaperProps } from '@mui/material/Paper';
import { useTheme, styled } from '@mui/material/styles';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import debounce from 'lodash/debounce';

import { IHierarchicalDepartmentDTO } from 'src/api/dtos';
import CommonDepartmentRepository from 'src/api/repositories/common/v1/department.repository';
import { getAllChildDepartmentIds } from 'src/features/departments/departments.utils';
import { SearchBar, SearchIcon } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';

import { MultiDepartmentSelectorPaperExtraProps } from './MultiDepartmentSelector.types';
import { MultiDepartmentSelectorOption } from './MultiDepartmentSelectorOption';
import { MultiDepartmentSelectorPaper } from './MultiDepartmentSelectorPaper';

interface Props {
  ids?: number[];
  onChange?: (ids: number[]) => void;
}

export const MultiDepartmentSelector: FC<Props> = ({ ids = [], onChange }) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const {
    data: departments,
    isLoading,
    isFetching,
  } = CommonDepartmentRepository.endpoints.useGetDepartmentsQuery(
    { searchTerm },
    {
      skip: !open,
      selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
    },
  );

  const loading = isLoading || isFetching;

  const debounceSearchTermChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const handleInputValueChange = (_: SyntheticEvent, value: string) => {
    debounceSearchTermChange(value);
    setInputValue(value);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckedChange = (checked: number[]) => {
    onChange?.(checked);
  };

  const renderDepartmentOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: IHierarchicalDepartmentDTO,
    state: AutocompleteRenderOptionState,
    ownerState: AutocompleteOwnerState<IHierarchicalDepartmentDTO, boolean, boolean, false, 'div'>,
  ) => {
    const checked = ids.includes(option.id);

    const childDepartmentIds = getAllChildDepartmentIds(option);
    const indeterminate = ids.some((checkedId) => childDepartmentIds.includes(checkedId));

    return (
      <StyledTreeItem
        key={option.id}
        nodeId={option.id.toString()}
        label={
          <MultiDepartmentSelectorOption
            department={option}
            checked={checked}
            indeterminate={indeterminate && !checked}
          />
        }
      >
        {option.children?.length
          ? option.children.map((child) => renderDepartmentOption(props, child, state, ownerState))
          : null}
      </StyledTreeItem>
    );
  };

  return (
    <SearchBar
      // Open
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      // Options
      options={departments}
      getOptionLabel={(option) => `${option.id}`}
      renderOption={renderDepartmentOption}
      filterOptions={(options) => options} // Don't filter options with inputValue
      // Loading
      loading={loading}
      // Input
      inputValue={inputValue}
      onInputChange={handleInputValueChange}
      InputProps={{
        startAdornment: (
          <>
            <SearchIcon color={theme.palette.action.active} />
          </>
        ),
      }}
      // Paper
      PaperComponent={MultiDepartmentSelectorPaper}
      // Props
      slotProps={{
        paper: {
          onMouseDown: (event) => {
            event.stopPropagation();
            event.preventDefault();
          },
          departments,
          ids,
          onCheckedChange: handleCheckedChange,
        } as PaperProps & MultiDepartmentSelectorPaperExtraProps,
      }}
      // Settings
      placeholder={t('departments.searchDepartments')}
      disableCloseOnSelect
    />
  );
};

const StyledTreeItem = styled(TreeItem)(() => ({
  '.MuiTreeItem-content': {
    flexDirection: 'row-reverse',
    padding: '0 16px',
    height: 36,

    '.MuiTreeItem-iconContainer': {
      margin: 0,
      alignItems: 'center',
      height: 36,
      width: 36,
      padding: 0,
    },

    '.MuiTreeItem-label': {
      padding: 0,
      fontSize: 14,
    },
  },
}));
